import { Box, Container } from "@mui/material";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { useAppDispatch } from "../hooks/store";
import { useLazyGetUserQuery } from "../service/auth";
import { setCredentials } from "../reducers/authSlice";
import { SIDEBAR_WIDTH, STORAGE_KEYS } from "../constants";
import { getFromStorage } from "../helpers";
import Loader from "../constants/Loader";
import Sidebar from "./SideBar";
import Topbar from "./Topbar";

interface MainContainerProps {
  hideHeader?: boolean;
}

const MainContainer = memo(({ hideHeader = false }: MainContainerProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [getUser, { isLoading }] = useLazyGetUserQuery();

  const token = useMemo(() => getFromStorage(STORAGE_KEYS.token), []);

  const handleSidebarToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const getUserDetails = useCallback(async () => {
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const result = await getUser({}).unwrap();
      if (result?.code === 200) {
        secureLocalStorage.setItem("user", result.data);
        dispatch(setCredentials({ user: result.data, token }));
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setIsAuthenticated(false);
    }
  }, [token, dispatch, getUser]);

  // Check authentication status on mount
  useEffect(() => {
    if (!token) {
      setIsAuthenticated(false);
      return;
    }
    getUserDetails();
  }, [token, getUserDetails]);

  // Redirect if not authenticated
  useEffect(() => {
    if (isAuthenticated === false) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Show loading state while checking auth
  if (isAuthenticated === null) {
    return <Loader isLoad={true} />;
  }

  // Redirect if not authenticated (fallback)
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {!hideHeader && <Topbar handleSidebarToggle={handleSidebarToggle} />}
      <Sidebar
        mobileOpen={mobileOpen}
        handleSidebarToggle={handleSidebarToggle}
      />
      <Box
        component="main"
        className="main_content"
        sx={{
          marginLeft: "auto",
          width: { lg: `calc(100% - ${SIDEBAR_WIDTH}px)` },
        }}
      >
        <Loader isLoad={isLoading} />
        <Container className="sub-layout">
          <Outlet />
        </Container>
      </Box>
    </>
  );
});

MainContainer.displayName = "MainContainer";

export default MainContainer;
